
import React, { useState, useEffect, useRef } from 'react';
import { 
  X, Target, Activity, Scale, Terminal, Copy, 
  Download, FileJson, Database, ShieldAlert, Brain, History, CheckSquare, Square, AlertTriangle, ArrowRight, ArrowLeft, Calculator, Euro, Users, Clock, Crosshair, Shield, Search, List, Edit2, Trash2, Save, Plus, MinusCircle
} from 'lucide-react';
import { fetchEntryDetails, CisoEntryDetail, generateWordPressXML, saveEntryToWordPress, deleteAtlasEntry } from '../services/dataService';
import { askOracle } from '../services/geminiService';

interface AtlasCardProps {
  term: string;
  onClose: () => void;
  isAdmin?: boolean;
  onDataChange?: () => void;
}

const AtlasCard: React.FC<AtlasCardProps> = ({ term, onClose, isAdmin = false, onDataChange }) => {
  const [data, setData] = useState<CisoEntryDetail | null>(null);
  const [loading, setLoading] = useState(true);
  
  // EDIT STATE
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<CisoEntryDetail> | null>(null);

  // GOVERNANCE STATE (Interactive)
  const [auditState, setAuditState] = useState<any[]>([]);
  const [complianceScore, setComplianceScore] = useState(0);

  // RISK SIMULATOR STATE (New)
  const [simRevenue, setSimRevenue] = useState(10000000); // 10M Default
  const [simRecords, setSimRecords] = useState(5000); // 5k Records
  const [simDowntime, setSimDowntime] = useState(24); // 24h Downtime
  const [calculatedLoss, setCalculatedLoss] = useState(0);

  // DASHBOARD STATE
  const [viewMode, setViewMode] = useState<'STRATEGY' | 'OPS' | 'GOVERNANCE'>('STRATEGY');

  // SYSOP (TELETYPE) STATE
  const [sysopHistory, setSysopHistory] = useState<{role: 'user' | 'sysop', text: React.ReactNode}[]>([]);
  const [sysopInput, setSysopInput] = useState('');
  const [sysopProcessing, setSysopProcessing] = useState(false);
  const sysopEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(true);
    fetchEntryDetails(term).then(result => {
        setData(result);
        // Initialize Audit State locally for interactivity
        setAuditState(result.governance.auditChecklist);
        calculateCompliance(result.governance.auditChecklist);
        
        // Init Edit Form
        setEditForm(JSON.parse(JSON.stringify(result)));

        setLoading(false);
        setSysopHistory([{
            role: 'sysop',
            text: "SYSOP ONLINE. CHIEDI SUPPORTO STRATEGICO O SOLUZIONI A ERRORI COMUNI."
        }]);
    });
  }, [term]);

  useEffect(() => {
      if (!data) return;

      const costPerRecord = 145; 
      const hourlyRevenue = simRevenue / 2000; // Approx working hours/year
      const downtimeCost = hourlyRevenue * simDowntime * 1.5; // 1.5x factor for chaos/recovery
      const breachCost = simRecords * costPerRecord;
      
      // Regulation Fine Factor (if Critical)
      let fine = 0;
      if (data.header.type === 'Normativa' || data.header.riskCurrent.label === 'Critical') {
           fine = simRevenue * 0.02;
      }

      setCalculatedLoss(Math.round(downtimeCost + breachCost + (fine * 0.1))); 
  }, [simRevenue, simRecords, simDowntime, data]);

  useEffect(() => {
      sysopEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [sysopHistory, sysopProcessing]);

  const calculateCompliance = (list: any[]) => {
      if (list.length === 0) {
          setComplianceScore(0);
          return;
      }
      const checked = list.filter(i => i.status).length;
      setComplianceScore(Math.round((checked / list.length) * 100));
  };

  const toggleAuditItem = (index: number) => {
      if (isEditing) return; // Disable toggle in edit mode to prevent conflict
      const newState = [...auditState];
      newState[index].status = !newState[index].status;
      setAuditState(newState);
      calculateCompliance(newState);
  };

  // --- EDITING HELPERS ---
  const updateNestedField = (path: string[], value: any) => {
      setEditForm(prev => {
          const newState = JSON.parse(JSON.stringify(prev));
          let current = newState;
          for (let i = 0; i < path.length - 1; i++) {
              if (!current[path[i]]) current[path[i]] = {};
              current = current[path[i]];
          }
          current[path[path.length - 1]] = value;
          return newState;
      });
  };

  const updateArrayItem = (path: string[], index: number, field: string, value: any) => {
      setEditForm(prev => {
          const newState = JSON.parse(JSON.stringify(prev));
          let current = newState;
          for (const key of path) {
              current = current[key];
          }
          current[index][field] = value;
          return newState;
      });
  };

  const addArrayItem = (path: string[], emptyItem: any) => {
      setEditForm(prev => {
          const newState = JSON.parse(JSON.stringify(prev));
          let current = newState;
          for (const key of path) {
              current = current[key];
          }
          current.push(emptyItem);
          return newState;
      });
  };

  const removeArrayItem = (path: string[], index: number) => {
      setEditForm(prev => {
          const newState = JSON.parse(JSON.stringify(prev));
          let current = newState;
          for (const key of path) {
              current = current[key];
          }
          current.splice(index, 1);
          return newState;
      });
  };

  // --- ADMIN ACTIONS ---
  const handleSave = async () => {
      if (!editForm || !data) return;
      
      const updatedData = {
          ...data,
          ...editForm,
      };
      
      const success = await saveEntryToWordPress(updatedData as CisoEntryDetail);
      if (success) {
          setData(updatedData as CisoEntryDetail);
          // Update local audit state if changed
          if(editForm.governance?.auditChecklist) {
              setAuditState(editForm.governance.auditChecklist);
              calculateCompliance(editForm.governance.auditChecklist);
          }
          setIsEditing(false);
          alert("Modifiche salvate con successo.");
          if (onDataChange) onDataChange();
      } else {
          alert("Errore durante il salvataggio.");
      }
  };

  const handleDelete = async () => {
      if (!data) return;
      if (confirm(`SEI SICURO DI VOLER ELIMINARE LA SCHEDA "${data.header.title}"? Questa azione è irreversibile.`)) {
          const success = await deleteAtlasEntry(data.header.title);
          if (success) {
              alert("Scheda eliminata.");
              if (onDataChange) onDataChange();
              onClose();
          } else {
              alert("Errore durante l'eliminazione.");
          }
      }
  };

  // --- RATE LIMITER LOGIC ---
  const checkDailyQuota = (): boolean => {
      const STORAGE_KEY = 'sysop_daily_quota';
      const MAX_QUERIES = 3;
      const today = new Date().toLocaleDateString();
      
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return true;

      const usage = JSON.parse(stored);
      if (usage.date !== today) return true; 

      return usage.count < MAX_QUERIES;
  };

  const incrementDailyQuota = () => {
      const STORAGE_KEY = 'sysop_daily_quota';
      const today = new Date().toLocaleDateString();
      const stored = localStorage.getItem(STORAGE_KEY);
      
      let count = 0;
      if (stored) {
          const usage = JSON.parse(stored);
          if (usage.date === today) count = usage.count;
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify({
          date: today,
          count: count + 1
      }));
  };

  const handleSysopSubmit = async (inputOverride?: string) => {
      const msgToSend = inputOverride || sysopInput;
      if (!msgToSend.trim() || sysopProcessing || !data) return;

      setSysopInput('');
      setSysopHistory(prev => [...prev, { role: 'user', text: `> ${msgToSend}` }]);

      if (!checkDailyQuota()) {
          setTimeout(() => {
              setSysopHistory(prev => [...prev, { 
                  role: 'sysop', 
                  text: <span className="text-red-600">ACCESS DENIED. LIMITE GIORNALIERO RAGGIUNTO (3/3). IL SISTEMA NECESSITA DI RAFFREDDAMENTO. RIPROVA DOMANI.</span> 
              }]);
          }, 500);
          return;
      }

      setSysopProcessing(true);

      const contextQuery = `
        [CONTESTO: ${data.header.title}]
        [RUOLO: Sysop Esperto CyberSecurity]
        
        UTENTE: ${msgToSend}
        
        ISTRUZIONI:
        1. Rispondi in modo sintetico (max 4 righe).
        2. Focalizzati su ERRORI COMUNI che si commettono in questo ambito.
        3. Fornisci una SOLUZIONE PRATICA o un rimedio immediato.
        4. Usa un tono tecnico ma imperativo.
        
        Rispondi in Italiano.
      `;
      
      const response = await askOracle(contextQuery);
      
      setSysopHistory(prev => [...prev, { role: 'sysop', text: response.toUpperCase() }]);
      setSysopProcessing(false);
      incrementDailyQuota();
  };

  const handleExportXml = () => {
      if(data) {
          const exportData = {
              ...data,
              governance: {
                  ...data.governance,
                  auditChecklist: auditState
              }
          };
          generateWordPressXML(exportData);
          setSysopHistory(prev => [...prev, { role: 'sysop', text: "DATI ESPORTATI CORRETTAMENTE IN XML (COMPATIBILE WP)." }]);
      }
  };

  const formatCurrency = (val: number) => {
      return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(val);
  };
  
  const getRiskColor = (label: string) => {
      switch(label) {
          case 'Critical': return 'text-red-600';
          case 'High': return 'text-orange-600';
          case 'Medium': return 'text-yellow-600';
          case 'Low': return 'text-green-600';
          default: return 'text-gray-600';
      }
  };

  if (loading || !data) return (
      <div className="fixed inset-0 z-[45] bg-white/80 backdrop-blur flex items-center justify-center font-sans text-sm uppercase tracking-widest pt-20 animate-fade-in">
          <div className="flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin"></div>
            Caricamento Modulo Dati...
          </div>
      </div>
  );

  return (
    <div className="fixed inset-0 z-[45] bg-gray-100/95 overflow-y-auto pt-24 pb-12 animate-fade-in font-sans text-black">
      
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 animate-slide-up" style={{animationDuration: '0.5s'}}>
        
        {/* Navigation Breadcrumb */}
        <div className="mb-6 flex justify-between items-center">
            <button onClick={onClose} className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-500 hover:text-black transition-colors group">
                <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                Torna all'Atlante
            </button>
            
            {/* ADMIN TOOLBAR */}
            {isAdmin && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 px-3 py-1 animate-fade-in">
                    <span className="text-[9px] font-bold text-red-600 uppercase tracking-widest mr-2">Admin Mode</span>
                    {!isEditing ? (
                        <>
                            <button onClick={() => setIsEditing(true)} className="p-1 hover:bg-red-100 text-black rounded" title="Modifica Scheda">
                                <Edit2 size={14} />
                            </button>
                            <button onClick={handleDelete} className="p-1 hover:bg-red-100 text-red-600 rounded" title="Elimina Scheda">
                                <Trash2 size={14} />
                            </button>
                        </>
                    ) : (
                        <>
                            <button onClick={handleSave} className="p-1 hover:bg-green-100 text-green-700 rounded flex items-center gap-1">
                                <Save size={14} /> <span className="text-[10px] font-bold">SALVA</span>
                            </button>
                            <button onClick={() => setIsEditing(false)} className="p-1 hover:bg-red-100 text-black rounded">
                                <X size={14} />
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>

        {/* --- HEADER CARD --- */}
        <div className="bg-white border border-black p-6 mb-8 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
             <div className="w-full md:w-auto">
                <div className="flex flex-wrap items-center gap-y-2 gap-x-4 mb-3 text-[10px] font-bold uppercase tracking-widest text-gray-500">
                    <span className="text-black">{data.header.id}</span>
                    <span className="w-px h-3 bg-gray-300"></span>
                    <span className="flex items-center gap-1 text-gray-400"><Clock size={12}/> {data.header.lastUpdated}</span>
                    <span className="w-px h-3 bg-gray-300"></span>
                    
                    {isEditing ? (
                        <select 
                            className="border border-gray-300 bg-white text-black p-1 text-[10px]"
                            value={editForm?.header?.type}
                            onChange={e => updateNestedField(['header', 'type'], e.target.value)}
                        >
                            <option>Minaccia</option>
                            <option>Vulnerabilità</option>
                            <option>Controllo</option>
                            <option>Concetto</option>
                            <option>Normativa</option>
                            <option>Modello AI</option>
                        </select>
                    ) : (
                        <span className="border border-gray-300 px-1.5 py-0.5 rounded text-black font-bold">{data.header.type}</span>
                    )}

                    <span className="w-px h-3 bg-gray-300"></span>
                    {isEditing ? (
                        <select 
                            className="border border-gray-300 bg-white text-black p-1 text-[10px]"
                            value={editForm?.header?.riskCurrent?.label}
                            onChange={e => updateNestedField(['header', 'riskCurrent', 'label'], e.target.value)}
                        >
                            <option>Critical</option>
                            <option>High</option>
                            <option>Medium</option>
                            <option>Low</option>
                        </select>
                    ) : (
                        <span className={`font-bold ${getRiskColor(data.header.riskCurrent.label)}`}>
                            SEVERITY: {data.header.riskCurrent.label}
                        </span>
                    )}
                </div>
                
                {isEditing ? (
                    <input 
                        className="text-3xl md:text-5xl font-serif font-bold tracking-tight leading-none w-full border-b-2 border-black outline-none bg-gray-50"
                        value={editForm?.header?.title}
                        onChange={e => updateNestedField(['header', 'title'], e.target.value)}
                    />
                ) : (
                    <h1 className="text-3xl md:text-5xl font-serif font-bold tracking-tight leading-none">{data.header.title}</h1>
                )}
             </div>
             <div className="flex gap-3 shrink-0">
                <button onClick={handleExportXml} className="flex items-center gap-2 px-4 py-2 border border-gray-200 hover:border-black hover:bg-black hover:text-white transition-all text-xs font-bold uppercase tracking-widest">
                    <FileJson size={14} /> XML Export
                </button>
                <button onClick={onClose} className="flex items-center gap-2 px-4 py-2 bg-black text-white hover:bg-red-600 transition-colors text-xs font-bold uppercase tracking-widest">
                    <X size={14} /> Chiudi
                </button>
             </div>
        </div>

        {/* --- MAIN LAYOUT --- */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            <div className="lg:col-span-2 space-y-8">
                
                {/* 1. CONCEPT & METRICS */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white border border-gray-200 p-6 shadow-sm flex flex-col">
                        <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4 flex items-center gap-2">
                            <Brain size={14} /> Definizione Operativa
                        </div>
                        {isEditing ? (
                            <textarea 
                                className="text-lg font-serif italic leading-relaxed mb-6 flex-grow text-gray-800 w-full h-full border p-2 bg-gray-50 outline-none resize-none"
                                value={editForm?.genesis?.definition}
                                onChange={e => updateNestedField(['genesis', 'definition'], e.target.value)}
                            />
                        ) : (
                            <p className="text-lg font-serif italic leading-relaxed mb-6 flex-grow text-gray-800">
                                {data.genesis.definition}
                            </p>
                        )}
                        <div className="mt-auto pt-4 border-t border-gray-100">
                            <div className="text-[10px] text-gray-400 uppercase mb-1 font-bold tracking-widest">Archetipo</div>
                            {isEditing ? (
                                <input 
                                    className="text-sm font-bold border-b border-gray-300 w-full"
                                    value={editForm?.genesis?.archetype}
                                    onChange={e => updateNestedField(['genesis', 'archetype'], e.target.value)}
                                />
                            ) : (
                                <div className="text-sm font-bold">{data.genesis.archetype}</div>
                            )}
                        </div>
                    </div>

                    <div className="bg-white border border-gray-200 p-6 shadow-sm">
                        <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4 flex items-center gap-2">
                            <Activity size={14} /> Risk Assessment
                        </div>
                        <div className="flex items-center justify-between h-full pb-4">
                             <div className="text-center">
                                 {isEditing ? (
                                    <input 
                                        type="number"
                                        min="0" max="5" step="0.1"
                                        className="text-5xl font-bold tracking-tighter text-black w-24 text-center border-b border-black outline-none bg-gray-50"
                                        value={editForm?.header?.riskCurrent?.score}
                                        onChange={e => updateNestedField(['header', 'riskCurrent', 'score'], Number(e.target.value))}
                                    />
                                 ) : (
                                     <div className={`text-5xl font-bold tracking-tighter ${data.header.riskCurrent.score > 3 ? 'text-red-600' : 'text-green-600'}`}>
                                         {data.header.riskCurrent.score}
                                     </div>
                                 )}
                                 <div className="text-[9px] uppercase font-bold tracking-widest text-gray-500 mt-1">Risk Score</div>
                             </div>
                             <div className="w-px h-16 bg-gray-200"></div>
                             <div className="text-center">
                                 {isEditing ? (
                                     <input 
                                        type="number"
                                        className="text-5xl font-bold tracking-tighter text-black w-24 text-center border-b border-black outline-none bg-gray-50"
                                        value={editForm?.header?.exposure}
                                        onChange={e => updateNestedField(['header', 'exposure'], Number(e.target.value))}
                                     />
                                 ) : (
                                     <div className="text-5xl font-bold tracking-tighter text-black">
                                         {data.header.exposure}<span className="text-xl">%</span>
                                     </div>
                                 )}
                                 <div className="text-[9px] uppercase font-bold tracking-widest text-gray-500 mt-1">Exposure</div>
                             </div>
                        </div>
                    </div>
                </div>

                {/* 2. MAIN TABS & CONTENT */}
                <div className="bg-white border border-gray-200 shadow-sm min-h-[500px]">
                    <div className="flex border-b border-gray-200 bg-gray-50">
                         <button onClick={() => setViewMode('STRATEGY')} className={`flex-1 py-4 text-xs font-bold uppercase tracking-widest border-b-2 transition-colors ${viewMode === 'STRATEGY' ? 'border-black bg-white text-black' : 'border-transparent text-gray-400 hover:text-black'}`}>Strategia</button>
                         <button onClick={() => setViewMode('OPS')} className={`flex-1 py-4 text-xs font-bold uppercase tracking-widest border-b-2 transition-colors ${viewMode === 'OPS' ? 'border-black bg-white text-black' : 'border-transparent text-gray-400 hover:text-black'}`}>Tattica (Ops)</button>
                         <button onClick={() => setViewMode('GOVERNANCE')} className={`flex-1 py-4 text-xs font-bold uppercase tracking-widest border-b-2 transition-colors ${viewMode === 'GOVERNANCE' ? 'border-black bg-white text-black' : 'border-transparent text-gray-400 hover:text-black'}`}>Governance</button>
                    </div>

                    <div className="p-6 md:p-8">
                        {/* STRATEGY */}
                        {viewMode === 'STRATEGY' && (
                            <div className="animate-fade-in space-y-8">
                                <div className="border border-black p-6 bg-white shadow-[4px_4px_0px_rgba(0,0,0,0.1)]">
                                    <h3 className="text-sm font-bold uppercase mb-2 flex items-center gap-2 text-black tracking-widest">
                                        <AlertTriangle size={14} /> Worst Case Scenario
                                    </h3>
                                    {isEditing ? (
                                        <textarea 
                                            className="text-gray-800 mb-4 leading-relaxed font-serif text-lg w-full border p-2 bg-gray-50 h-32"
                                            value={editForm?.overview?.worstCaseScenario?.description}
                                            onChange={e => updateNestedField(['overview', 'worstCaseScenario', 'description'], e.target.value)}
                                        />
                                    ) : (
                                        <p className="text-gray-800 mb-4 leading-relaxed font-serif text-lg">{data.overview.worstCaseScenario.description}</p>
                                    )}
                                    <div className="text-xs font-bold text-white bg-black inline-block px-3 py-1 uppercase tracking-widest">
                                        IMPATTO CRITICO
                                    </div>
                                </div>

                                {/* Risk Simulator (Client side, mostly interactive) */}
                                <div className="border border-gray-200 bg-gray-50 p-6 rounded-sm">
                                    <h3 className="text-xs font-bold uppercase border-b border-gray-300 pb-2 mb-4 text-gray-600 flex items-center gap-2 tracking-widest">
                                        <Calculator size={14}/> Simulatore Impatto Finanziario
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                        <div>
                                            <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1 tracking-widest">Fatturato Annuo (€)</label>
                                            <input type="number" value={simRevenue} onChange={(e) => setSimRevenue(Number(e.target.value))} className="w-full p-2 border border-gray-300" />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1 tracking-widest">Record / Utenti</label>
                                            <input type="number" value={simRecords} onChange={(e) => setSimRecords(Number(e.target.value))} className="w-full p-2 border border-gray-300" />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1 tracking-widest">Downtime (Ore)</label>
                                            <input type="number" value={simDowntime} onChange={(e) => setSimDowntime(Number(e.target.value))} className="w-full p-2 border border-gray-300" />
                                        </div>
                                    </div>
                                    <div className="bg-black text-white p-4 flex justify-between items-center">
                                        <div>
                                            <div className="text-[9px] uppercase font-bold tracking-widest text-gray-400">Perdita Stimata (ALE)</div>
                                        </div>
                                        <div className="text-2xl font-bold">{formatCurrency(calculatedLoss)}</div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div>
                                        <h3 className="text-xs font-bold uppercase border-b border-gray-200 pb-2 mb-4 text-gray-400 tracking-widest">Parametri Predefiniti</h3>
                                        <div className="space-y-4">
                                            <div className="flex justify-between text-sm border-b border-gray-100 pb-2">
                                                <span className="text-gray-600">Costo Prevenzione Est.</span>
                                                {isEditing ? (
                                                    <input className="font-bold w-24 text-right border-b" value={editForm?.risk?.financials?.preventionCost} onChange={e => updateNestedField(['risk', 'financials', 'preventionCost'], e.target.value)} />
                                                ) : (
                                                    <span className="font-bold">{data.risk.financials.preventionCost}</span>
                                                )}
                                            </div>
                                            <div className="flex justify-between text-sm border-b border-gray-100 pb-2">
                                                <span className="text-gray-600">ROI Stimato</span>
                                                {isEditing ? (
                                                    <input className="font-bold text-green-600 w-24 text-right border-b" value={editForm?.risk?.financials?.roi} onChange={e => updateNestedField(['risk', 'financials', 'roi'], e.target.value)} />
                                                ) : (
                                                    <span className="font-bold text-green-600">{data.risk.financials.roi}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xs font-bold uppercase border-b border-gray-200 pb-2 mb-4 text-gray-400 tracking-widest">Decision Board</h3>
                                        <div className="bg-white border border-black text-black p-6 text-center shadow-sm">
                                            {isEditing ? (
                                                <select className="text-2xl font-bold tracking-widest mb-2 border p-1" value={editForm?.risk?.decisionBoard?.action} onChange={e => updateNestedField(['risk', 'decisionBoard', 'action'], e.target.value)}>
                                                    <option>MITIGATE</option><option>ACCEPT</option><option>TRANSFER</option><option>AVOID</option>
                                                </select>
                                            ) : (
                                                <div className="text-3xl font-bold tracking-widest mb-2">{data.risk.decisionBoard.action}</div>
                                            )}
                                            {isEditing ? (
                                                <textarea className="w-full text-xs italic border p-1" value={editForm?.risk?.decisionBoard?.rationale} onChange={e => updateNestedField(['risk', 'decisionBoard', 'rationale'], e.target.value)} />
                                            ) : (
                                                <div className="mt-2 text-xs text-gray-600 italic leading-relaxed border-t border-gray-100 pt-2 font-serif">"{data.risk.decisionBoard.rationale}"</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* OPS */}
                        {viewMode === 'OPS' && (
                            <div className="animate-fade-in">
                                <div className="mb-8">
                                    <h3 className="text-xs font-bold uppercase border-b border-gray-200 pb-2 mb-4 text-gray-400 flex items-center gap-2 tracking-widest">
                                        <List size={14}/> Matrice MITRE ATT&CK
                                    </h3>
                                    {isEditing ? (
                                        <input 
                                            className="w-full border p-2 text-xs font-mono" 
                                            value={editForm?.ops?.mitre?.join(', ')} 
                                            onChange={e => updateNestedField(['ops', 'mitre'], e.target.value.split(',').map(s => s.trim()))}
                                            placeholder="T1566, T1000..."
                                        />
                                    ) : (
                                        <div className="flex flex-wrap gap-2">
                                            {data.ops.mitre.map(m => (
                                                <span key={m} className="px-3 py-1 bg-white border border-black text-black text-xs font-bold">
                                                    {m}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                                    {/* RED TEAM */}
                                    <div className="border border-red-100 bg-red-50/30 p-4">
                                        <h3 className="text-xs font-bold uppercase mb-4 text-red-700 flex items-center justify-between tracking-widest">
                                            <span className="flex items-center gap-2"><Target size={14}/> RED TEAM</span>
                                            {isEditing && (
                                                <button onClick={() => addArrayItem(['ops', 'redTeam'], {useCase: 'New Scenario', entryPoint: 'Vector', successRate: 50})} className="bg-red-200 hover:bg-red-300 p-1 rounded text-red-800">
                                                    <Plus size={14} />
                                                </button>
                                            )}
                                        </h3>
                                        <div className="space-y-3">
                                            {editForm?.ops?.redTeam?.map((rt: any, i: number) => (
                                                <div key={i} className="bg-white p-3 border border-red-100 shadow-sm relative group">
                                                    {isEditing ? (
                                                        <div className="space-y-2">
                                                            <div className="flex justify-between">
                                                                <input className="text-xs font-bold w-full border-b mr-2" value={rt.entryPoint} onChange={e => updateArrayItem(['ops', 'redTeam'], i, 'entryPoint', e.target.value)} placeholder="Entry Point" />
                                                                <button onClick={() => removeArrayItem(['ops', 'redTeam'], i)} className="text-red-500"><MinusCircle size={14}/></button>
                                                            </div>
                                                            <input className="text-xs italic w-full border-b" value={rt.useCase} onChange={e => updateArrayItem(['ops', 'redTeam'], i, 'useCase', e.target.value)} placeholder="Scenario" />
                                                            <div className="flex items-center gap-2 text-xs">
                                                                <span>Success Rate:</span>
                                                                <input type="number" className="w-12 border p-1" value={rt.successRate} onChange={e => updateArrayItem(['ops', 'redTeam'], i, 'successRate', Number(e.target.value))} />
                                                                <span>%</span>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div className="text-[10px] text-gray-400 uppercase font-bold tracking-widest">Vettore d'ingresso</div>
                                                            <div className="font-bold text-sm mb-1">{rt.entryPoint}</div>
                                                            <div className="text-xs text-gray-600 italic font-serif">"{rt.useCase}"</div>
                                                            <div className="mt-2 h-1 w-full bg-gray-100 rounded-full overflow-hidden">
                                                                <div className="h-full bg-red-500" style={{width: `${rt.successRate}%`}}></div>
                                                            </div>
                                                            <div className="text-[9px] text-right text-red-500 mt-1 font-bold">Probabilità Successo: {rt.successRate}%</div>
                                                        </>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* BLUE TEAM */}
                                    <div className="border border-blue-100 bg-blue-50/30 p-4">
                                        <h3 className="text-xs font-bold uppercase mb-4 text-blue-700 flex items-center justify-between tracking-widest">
                                            <span className="flex items-center gap-2"><Shield size={14}/> BLUE TEAM</span>
                                            {isEditing && (
                                                <button onClick={() => addArrayItem(['ops', 'blueTeam'], {detectionRule: 'New Rule', type: 'Signature', coverage: 50})} className="bg-blue-200 hover:bg-blue-300 p-1 rounded text-blue-800">
                                                    <Plus size={14} />
                                                </button>
                                            )}
                                        </h3>
                                        <div className="space-y-3">
                                            {editForm?.ops?.blueTeam?.map((bt: any, i: number) => (
                                                <div key={i} className="bg-white p-3 border border-blue-100 shadow-sm relative group">
                                                    {isEditing ? (
                                                        <div className="space-y-2">
                                                            <div className="flex justify-between">
                                                                <input className="text-xs font-bold w-full border-b mr-2" value={bt.detectionRule} onChange={e => updateArrayItem(['ops', 'blueTeam'], i, 'detectionRule', e.target.value)} placeholder="Rule Name" />
                                                                <button onClick={() => removeArrayItem(['ops', 'blueTeam'], i)} className="text-red-500"><MinusCircle size={14}/></button>
                                                            </div>
                                                            <div className="flex items-center gap-2 text-xs">
                                                                <input className="w-20 border p-1" value={bt.type} onChange={e => updateArrayItem(['ops', 'blueTeam'], i, 'type', e.target.value)} placeholder="Type" />
                                                                <span>Cov:</span>
                                                                <input type="number" className="w-12 border p-1" value={bt.coverage} onChange={e => updateArrayItem(['ops', 'blueTeam'], i, 'coverage', Number(e.target.value))} />
                                                                <span>%</span>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div className="text-[10px] text-gray-400 uppercase font-bold tracking-widest">Regola di Rilevamento</div>
                                                            <div className="font-bold text-sm mb-1">{bt.detectionRule}</div>
                                                            <div className="flex justify-between items-center mt-2">
                                                                <span className="text-[10px] bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-bold uppercase">{bt.type}</span>
                                                                <span className="text-[10px] font-bold text-blue-600">Coverage: {bt.coverage}%</span>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-xs font-bold uppercase border-b border-gray-200 pb-2 mb-4 text-gray-400 flex items-center justify-between tracking-widest">
                                        <span className="flex items-center gap-2"><Search size={14}/> IoC</span>
                                        {isEditing && (
                                            <button onClick={() => addArrayItem(['ops', 'iocs'], {type: 'IP', value: '0.0.0.0', confidence: 'Low'})} className="bg-gray-200 hover:bg-gray-300 p-1 rounded text-black">
                                                <Plus size={14} />
                                            </button>
                                        )}
                                    </h3>
                                    <div className="bg-black text-green-400 p-4 text-xs overflow-x-auto border border-gray-800 font-sans">
                                        {editForm?.ops?.iocs?.map((ioc: any, i: number) => (
                                            <div key={i} className="mb-2 border-b border-gray-800 pb-1 last:border-0 flex justify-between items-center">
                                                {isEditing ? (
                                                    <div className="flex gap-2 w-full">
                                                        <input className="bg-gray-900 text-green-400 w-16 px-1" value={ioc.type} onChange={e => updateArrayItem(['ops', 'iocs'], i, 'type', e.target.value)} />
                                                        <input className="bg-gray-900 text-green-400 flex-1 px-1" value={ioc.value} onChange={e => updateArrayItem(['ops', 'iocs'], i, 'value', e.target.value)} />
                                                        <select className="bg-gray-900 text-green-400 px-1" value={ioc.confidence} onChange={e => updateArrayItem(['ops', 'iocs'], i, 'confidence', e.target.value)}>
                                                            <option>High</option><option>Med</option><option>Low</option>
                                                        </select>
                                                        <button onClick={() => removeArrayItem(['ops', 'iocs'], i)} className="text-red-500"><X size={14}/></button>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div>
                                                            <span className="text-white font-bold tracking-wide">[{ioc.type}]</span> {ioc.value} <span className="text-gray-500 ml-2 font-bold">// CONF: {ioc.confidence}</span>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* GOVERNANCE */}
                        {viewMode === 'GOVERNANCE' && (
                            <div className="animate-fade-in space-y-8">
                                <div className="bg-gray-50 p-6 border border-gray-200">
                                    <div className="flex justify-between items-end mb-2">
                                        <span className="text-xs font-bold uppercase tracking-widest">Compliance Attuale</span>
                                        <span className={`text-3xl font-bold ${
                                            complianceScore < 50 ? 'text-red-600' : complianceScore < 80 ? 'text-yellow-600' : 'text-green-600'
                                        }`}>{complianceScore}%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 h-3 rounded-full overflow-hidden">
                                        <div 
                                            className={`h-full transition-all duration-700 ${
                                                complianceScore < 50 ? 'bg-red-500' : complianceScore < 80 ? 'bg-yellow-500' : 'bg-green-500'
                                            }`} 
                                            style={{width: `${complianceScore}%`}}
                                        ></div>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-xs font-bold uppercase border-b border-gray-200 pb-2 mb-4 text-gray-400 flex items-center justify-between tracking-widest">
                                        <span>Audit Checklist</span>
                                        {isEditing && (
                                            <button onClick={() => addArrayItem(['governance', 'auditChecklist'], {question: 'New Check', severity: 'Med', status: false, remediation: 'Fix it'})} className="bg-gray-200 hover:bg-gray-300 p-1 rounded text-black">
                                                <Plus size={14} />
                                            </button>
                                        )}
                                    </h3>
                                    <div className="space-y-3">
                                        {editForm?.governance?.auditChecklist?.map((item: any, i: number) => (
                                            <div 
                                                key={i} 
                                                onClick={() => !isEditing && toggleAuditItem(i)}
                                                className={`flex flex-col gap-2 p-4 border transition-all ${
                                                    item.status ? 'bg-green-50/50 border-green-200' : 'bg-white border-gray-200 hover:border-black'
                                                }`}
                                            >
                                                {isEditing ? (
                                                    <>
                                                        <div className="flex justify-between">
                                                            <input className="text-sm font-bold w-full border-b mb-2" value={item.question} onChange={e => updateArrayItem(['governance', 'auditChecklist'], i, 'question', e.target.value)} />
                                                            <button onClick={() => removeArrayItem(['governance', 'auditChecklist'], i)} className="text-red-500 ml-2"><MinusCircle size={14}/></button>
                                                        </div>
                                                        <div className="flex gap-2 text-xs">
                                                            <span>Sev:</span>
                                                            <select value={item.severity} onChange={e => updateArrayItem(['governance', 'auditChecklist'], i, 'severity', e.target.value)}>
                                                                <option>Critical</option><option>High</option><option>Med</option><option>Low</option>
                                                            </select>
                                                            <span>Fix:</span>
                                                            <input className="flex-1 border-b" value={item.remediation} onChange={e => updateArrayItem(['governance', 'auditChecklist'], i, 'remediation', e.target.value)} />
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="flex items-start gap-4">
                                                        <div className={`w-6 h-6 shrink-0 mt-0.5 flex items-center justify-center border transition-colors ${
                                                            item.status ? 'bg-green-600 border-green-600 text-white' : 'bg-white border-gray-300 text-transparent'
                                                        }`}>
                                                            <CheckSquare size={16} />
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className={`text-sm font-bold mb-1 ${item.status ? 'text-green-900 line-through opacity-70' : 'text-black'}`}>{item.question}</div>
                                                            <div className="flex items-center gap-2">
                                                                <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded tracking-wide ${
                                                                    item.severity === 'Critical' ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'
                                                                }`}>{item.severity}</span>
                                                            </div>
                                                            {!item.status && (
                                                                <div className="mt-3 text-xs font-bold text-red-700 flex items-center gap-2">
                                                                    <ArrowRight size={12} /> FIX: {item.remediation}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* 3. TIMELINE (HISTORY) */}
                <div className="bg-white border border-gray-200 p-6 shadow-sm">
                     <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-6 flex items-center gap-2">
                        <History size={14} /> Evoluzione Storica
                    </div>
                    <div className="relative border-l border-gray-300 ml-2 space-y-8 pb-2">
                        {data.genesis.history.map((h, i) => (
                            <div key={i} className="relative pl-8 group">
                                <div className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full bg-gray-300 group-hover:bg-black transition-colors ring-4 ring-white"></div>
                                <div className="text-xs font-bold text-gray-400 group-hover:text-black transition-colors mb-1 tracking-wide">{h.year}</div>
                                <div className="text-sm font-medium font-serif italic">{h.event}</div>
                            </div>
                        ))}
                    </div>
                </div>

            </div>

            {/* RIGHT COLUMN (STICKY SYSOP) */}
            <div className="lg:col-span-1">
                <div className="sticky top-24">
                    <div className="bg-white border border-black shadow-md flex flex-col overflow-hidden" style={{height: 'calc(100vh - 150px)', maxHeight: '800px'}}>
                        <div className="bg-black text-white px-4 py-3 text-[10px] font-bold uppercase tracking-widest flex justify-between items-center shrink-0">
                            <span className="flex items-center gap-2"><Terminal size={12} /> Live Sysop Console</span>
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto p-4 md:p-6 font-sans text-xs leading-relaxed bg-[url('https://www.transparenttextures.com/patterns/paper.png')] bg-gray-50 shadow-inner">
                            {sysopHistory.map((msg, i) => (
                                <div key={i} className={`mb-4 ${msg.role === 'user' ? 'text-gray-500 italic text-right border-r-2 border-gray-300 pr-2' : 'text-black font-bold'}`}>
                                    {msg.role === 'sysop' && <span className="text-[9px] bg-black text-white px-1 mr-2 tracking-wider">OP</span>}
                                    {msg.text}
                                </div>
                            ))}
                            {sysopProcessing && <div className="animate-pulse text-xs text-gray-400 font-bold tracking-wider">_ ANALYZING NEURAL NET...</div>}
                            <div ref={sysopEndRef}></div>
                        </div>

                        <div className="p-4 border-t border-gray-200 bg-white shrink-0">
                            <form onSubmit={(e) => {e.preventDefault(); handleSysopSubmit();}} className="flex items-center border-2 border-black bg-black px-3 py-3 focus-within:ring-2 focus-within:ring-gray-400 transition-all mb-3">
                                <span className="font-bold text-green-500 mr-2 animate-pulse">{'>'}</span>
                                <input 
                                    className="flex-1 outline-none text-sm uppercase placeholder-gray-600 bg-black text-white font-bold tracking-wide"
                                    placeholder="CHIEDI SOLUZIONI..."
                                    value={sysopInput}
                                    onChange={e => setSysopInput(e.target.value)}
                                />
                            </form>
                            <div className="flex flex-wrap gap-2">
                                {['ERRORI COMUNI', 'COME RISOLVERE', 'CHECKLIST', 'NEWS'].map(chip => (
                                    <button key={chip} onClick={() => handleSysopSubmit(chip)} className="text-[9px] font-bold border border-gray-300 px-2 py-1 uppercase hover:bg-black hover:text-white transition-colors bg-white text-black tracking-wider">
                                        {chip}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
      </div>
    </div>
  );
};

export default AtlasCard;
